import React from "react";
import { dequal } from "dequal/lite";
import {
  Icon,
  SectionContainer,
  SectionTitle,
  SectionTitleBody,
  SectionTitleHeading,
  IconName
} from "@silversea-ssc/ui-components-react";
import { CruiseQuery } from "types/graphql-types";
import * as styles from "./SectionBenefits.module.scss";
import { isPresent } from "src/utils/checks";

type AllInclusiveBenefits = NonNullable<
  CruiseQuery["cruise"]
>["allInclusiveBenefits"];
type BenefitGroup = AllInclusiveBenefits[number];
type Benefit = NonNullable<Required<BenefitGroup>["inclusions"][number]>;
type BenefitWithText = Omit<Benefit, "text"> & { text: { localized: string } };
type Props = {
  title: string | undefined;
  content: string | undefined;
  allInclusiveBenefits: AllInclusiveBenefits;
};

const SectionBenefits: React.FC<Props> = ({
  title,
  content,
  allInclusiveBenefits
}) => (
  <SectionContainer
    spacingTop="regular"
    spacingBottom="condensed"
    backgroundColor="productTypeColor01"
    dataContentName="section_benefits"
  >
    <SectionTitle variant="column">
      <SectionTitleHeading className={styles.sectionTitleHeading}>
        {title}
      </SectionTitleHeading>
      <SectionTitleBody>{content}</SectionTitleBody>
    </SectionTitle>
    <div className={styles.benefitGroups}>
      {allInclusiveBenefits.map((group) => (
        <BenefitGroup key={group._id} group={group} />
      ))}
    </div>
  </SectionContainer>
);

export default React.memo(SectionBenefits, dequal);

const BenefitGroup: React.FC<{ group: BenefitGroup }> = ({ group }) => (
  <div className={styles.benefitGroup}>
    <div className={styles.groupTitleSection}>
      <Icon name={group.icon as IconName} padding={false} size="1.5x" />{" "}
      <span className={styles.groupTitle}>{group.title?.localized}</span>
    </div>
    {(group.inclusions || [])
      .filter((item): item is BenefitWithText =>
        isPresent(item?.text?.localized)
      )
      .map((item) => (
        <Benefit key={item._key} benefit={item} />
      ))}
  </div>
);

const Benefit: React.FC<{ benefit: BenefitWithText }> = ({ benefit }) => (
  <div className={styles.benefit}>
    <Icon name="circle" padding={false} />
    <span className={styles.benefitText}>
      <HighlightedWrap condition={benefit.highlight}>
        {benefit.text.localized}
      </HighlightedWrap>
    </span>
  </div>
);

const HighlightedWrap: React.FC<
  React.PropsWithChildren<{ condition?: boolean }>
> = ({ condition, children }) =>
  condition ? (
    <strong>{children}</strong>
  ) : (
    <React.Fragment>{children}</React.Fragment>
  );
